<template>
  <div v-if="visible" class="modal-overlay">
    <div class="modal-contenido">
      <h4>{{ tituloModal }}</h4>
      <div class="formulario-inicial">
        <div class="formulario-opcion">
          <label :for="'nombre-' + modo">
            Nombre <small>(Nombre completo del {{ nombreNivelSiguiente ?? 'componente' }})</small>
          </label>
          <textarea
            :id="'nombre-' + modo"
            v-model="nombre"
            :disabled="loading"
            rows="3"
            class="nombre-textarea"
          ></textarea>
        </div>
      </div>
      <div class="formulario-opcion">
        <label :for="'descripcion-' + modo">
          Descripción <small>(opcional)</small>
        </label>
        <textarea
          :id="'descripcion-' + modo"
          v-model="descripcion"
          :disabled="loading"
        ></textarea>
      </div>

      <div class="formulario-opcion">
        <label :for="'enfoque-' + modo">
          Enfoque <small>(solo para aspectos que varían por tipo de programa)</small>
        </label>
        <select :id="'enfoque-' + modo" v-model="enfoque" :disabled="loading">
          <option v-for="e in ENFOQUES" :key="e.value" :value="e.value">{{ e.label }}</option>
        </select>
        <small class="enfoque-nota">
          “General” aplica siempre. Elige un enfoque específico para una variante.
        </small>
      </div>

      <div class="formulario-opcion" v-if="enfoque !== 'general'">
        <label :for="'grupo-' + modo">
          Grupo de peso compartido <small>(mismo nombre = mismo peso)</small>
        </label>
        <input
          :id="'grupo-' + modo"
          type="text"
          v-model="grupoVariante"
          :disabled="loading"
          placeholder="Ej: planta profesoral"
        />
        <small class="enfoque-nota">
          Pon el <strong>mismo</strong> grupo en todas las variantes de un mismo aspecto: comparten un único slot de peso. Usa grupos distintos si hay dos aspectos que varían en la misma rama.
        </small>
      </div>

      <div class="botones">
        <button class="btn salir" @click="$emit('cancelar')" :disabled="loading">Cancelar</button>
        <button
          class="btn guardar"
          @click="emitirAceptar"
          :disabled="loading || !puedeAceptar"
        >
          {{ textoBoton }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Formulario de alta y edición de un componente.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, computed, watch } from 'vue'
import { ENFOQUES, type Enfoque } from '@/utils/enfoque'

const props = defineProps<{
  visible: boolean
  modo: 'agregarNivel1' | 'agregarHijo' | 'modificar'
  loading: boolean
  nombrePadre?: string
  nombreComponente?: string
  nombreInicial?: string
  descripcionInicial?: string
  enfoqueInicial?: string | null
  grupoInicial?: string | null
  nombreNivelSiguiente?: string
}>()

const emit = defineEmits<{
  (e: 'aceptar', payload: { nombre: string; descripcion: string; enfoque: string; grupo_variante: string | null }): void
  (e: 'cancelar'): void
}>()

const nombre = ref('')
const descripcion = ref('')
const enfoque = ref<Enfoque>('general')
const grupoVariante = ref('')

// Cuando se abre el modal, inicializar los campos
watch(() => props.visible, (val) => {
  if (val) {
    nombre.value = props.nombreInicial ?? ''
    descripcion.value = props.descripcionInicial ?? ''
    enfoque.value = (props.enfoqueInicial as Enfoque) ?? 'general'
    grupoVariante.value = props.grupoInicial ?? ''
  }
})

const puedeAceptar = computed(() => !!nombre.value.trim())

const tituloModal = computed(() => {
  switch (props.modo) {
    case 'agregarNivel1': return `Agregar ${props.nombreNivelSiguiente ?? 'componente'}`
    case 'agregarHijo': return `Agregar ${props.nombreNivelSiguiente ?? 'componente'} a: ${props.nombrePadre ?? ''}`
    case 'modificar': return `Modificar componente: ${props.nombreComponente ?? ''}`
    default: return ''
  }
})

const textoBoton = computed(() => {
  if (props.loading) {
    return props.modo === 'modificar' ? 'Guardando…' : 'Creando…'
  }
  return 'Aceptar'
})

function emitirAceptar() {
  if (!puedeAceptar.value) return
  // El grupo solo aplica a variantes (enfoque específico); en General se limpia.
  const grupo = enfoque.value !== 'general' && grupoVariante.value.trim()
    ? grupoVariante.value.trim()
    : null
  emit('aceptar', {
    nombre: nombre.value.trim(),
    descripcion: descripcion.value?.trim() || '',
    enfoque: enfoque.value,
    grupo_variante: grupo
  })
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.modal-contenido {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  max-width: 660px;
  width: 100%;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
}

.modal-contenido h4 {
  margin: 0 0 1.25rem;
  font-size: 1rem;
  font-weight: 700;
  color: #111;
  line-height: 1.4;
  word-break: break-word;
}

.formulario-inicial {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

.formulario-opcion {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

input,
textarea,
select {
  width: 100%;
  box-sizing: border-box;
  padding: 0.75rem;
  border: 1px solid #e6e5e5;
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
  border-radius: var(--radius-md);
  font-size: 1rem;
  font-family: var(--font-family);
  background-color: #f9f9f9;
  margin-bottom: 12px;
}

.nombre-textarea {
  resize: none;
  line-height: 1.5;
}

.botones {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn.salir {
  background-color: white;
  border: 1px solid #ccc;
}

.btn.salir:hover {
  background-color: rgb(212, 26, 26);
  color: white;
}

.btn.guardar {
  background-color: #eea222;
  color: black;
}

.btn.guardar:hover {
  background-color: #0e69a0;
  cursor: pointer;
  color: white;
}

label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
}

label small {
  font-weight: normal;
  font-size: 0.85em;
  color: #666;
  margin-left: 4px;
}
input:focus, select:focus, textarea:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}
</style>
